import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { EmailLog } from "@/hooks/useEmailLogs";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import { Calendar, Clock, Mail, User, AlertCircle, CheckCircle2, XCircle, RotateCw } from "lucide-react";
import { isRetryable } from "@/components/RetryEmailDialog";

interface EmailLogDetailsDialogProps {
  log: EmailLog | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onRetry?: (log: EmailLog) => void;
}

const getEmailTypeBadge = (type: string) => {
  const variants: Record<string, { label: string; className: string }> = {
    confirmation: { label: 'Confirmation', className: 'bg-blue-500/10 text-blue-500 hover:bg-blue-500/20' },
    modification: { label: 'Modification', className: 'bg-orange-500/10 text-orange-500 hover:bg-orange-500/20' },
    cancellation: { label: 'Annulation', className: 'bg-red-500/10 text-red-500 hover:bg-red-500/20' },
  };
  return variants[type] || { label: type, className: '' };
};

const getStatusBadge = (status: string) => {
  if (status === 'sent') {
    return { icon: CheckCircle2, label: 'Envoyé', className: 'bg-green-500/10 text-green-500' };
  }
  return { icon: XCircle, label: 'Échoué', className: 'bg-red-500/10 text-red-500' };
};

export const EmailLogDetailsDialog = ({ log, open, onOpenChange, onRetry }: EmailLogDetailsDialogProps) => {
  if (!log) return null;

  const emailType = getEmailTypeBadge(log.email_type);
  const status = getStatusBadge(log.status);
  const StatusIcon = status.icon;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Détails de l'email</DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          {/* Status and Type */}
          <div className="flex gap-2">
            <Badge className={emailType.className}>
              {emailType.label}
            </Badge>
            <Badge className={status.className}>
              <StatusIcon className="w-3 h-3 mr-1" />
              {status.label}
            </Badge>
          </div>

          {/* Email Details */}
          <div className="space-y-3">
            <div className="flex items-start gap-3">
              <Mail className="w-4 h-4 mt-1 text-muted-foreground" />
              <div>
                <p className="text-sm font-medium">Destinataire</p>
                <p className="text-sm text-muted-foreground">{log.customer_email}</p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <User className="w-4 h-4 mt-1 text-muted-foreground" />
              <div>
                <p className="text-sm font-medium">Client</p>
                <p className="text-sm text-muted-foreground">{log.customer_name}</p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <Calendar className="w-4 h-4 mt-1 text-muted-foreground" />
              <div>
                <p className="text-sm font-medium">Réservation</p>
                <p className="text-sm text-muted-foreground">
                  {format(new Date(log.reservation_date), 'PPP', { locale: fr })} à {log.reservation_time.substring(0, 5)}
                </p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <Clock className="w-4 h-4 mt-1 text-muted-foreground" />
              <div>
                <p className="text-sm font-medium">Date d'envoi</p>
                <p className="text-sm text-muted-foreground">
                  {format(new Date(log.sent_at), 'PPP à HH:mm:ss', { locale: fr })}
                </p>
              </div>
            </div>
          </div>

          {/* Subject */}
          <div>
            <p className="text-sm font-medium mb-1">Sujet</p>
            <p className="text-sm text-muted-foreground bg-muted p-2 rounded">{log.subject}</p>
          </div>

          {/* Error Message */}
          {log.error_message && (
            <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-3">
              <div className="flex items-start gap-2">
                <AlertCircle className="w-4 h-4 text-destructive mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-destructive">Erreur</p>
                  <p className="text-sm text-destructive/80 mt-1">{log.error_message}</p>
                </div>
              </div>
            </div>
          )}

          {/* Technical Details */}
          <div className="border-t pt-4">
            <p className="text-sm font-medium mb-2">Détails techniques</p>
            <div className="space-y-1 text-xs text-muted-foreground">
              <p><span className="font-medium">ID Réservation:</span> {log.reservation_id}</p>
              {log.resend_email_id && (
                <p><span className="font-medium">ID Resend:</span> {log.resend_email_id}</p>
              )}
              {log.sent_by_user_id && (
                <p><span className="font-medium">Envoyé par:</span> {log.sent_by_user_id}</p>
              )}
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-2 pt-4 border-t">
            {onRetry && isRetryable(log) && (
              <Button variant="outline" onClick={() => onRetry(log)}>
                <RotateCw className="w-4 h-4 mr-2" />
                Réessayer
              </Button>
            )}
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Fermer
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
